import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Customer } from '@/firebase/services/customers.service'
import { useCurrentOrganizationStore } from './currentOrganization'
import { useCostCenterStore } from './costCenter'
import { useCostTypeStore } from './costType'
import { BaseStore } from './baseStore'
import { where, type QueryConstraint } from 'firebase/firestore'

export interface Customer {
  id?: string
  name: string
  document?: string
  email?: string
  phone?: string
  address?: {
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
  }
  bankInfo?: {
    bank?: string
    agency?: string
    account?: string
    accountType?: 'checking' | 'savings'
  }
  costCenters?: Array<{
    costCenterId: string
    costTypeId: string
    costCenterName: string
    costTypeName: string
  }>
  active?: boolean
  organizationId?: string
  createdAt?: string
  updatedAt?: string
}

class CustomerStore extends BaseStore {
  constructor() {
    super('customers')
  }

  async fetchCustomers() {
    const filters: QueryConstraint[] = [
      where('active', '==', true)
    ]
    return await this.fetchCollection<Customer>(filters)
  }

  async addCustomer(customer: Omit<Customer, 'id' | 'organizationId' | 'active' | 'createdAt' | 'updatedAt'>) {
    return await this.addDocument<Customer>(customer)
  }

  async updateCustomer(id: string, customer: Partial<Customer>) {
    return await this.updateDocument<Customer>(id, customer)
  }

  async deleteCustomer(id: string) {
    return await this.updateDocument(id, { active: false })
  }
}

export const useCustomerStore = defineStore('customer', () => {
  const customers = ref<Customer[]>([])
  const loading = ref(false)
  const currentOrganizationStore = useCurrentOrganizationStore()
  const costCenterStore = useCostCenterStore()
  const costTypeStore = useCostTypeStore()
  const customerStore = new CustomerStore()

  const fetchCustomers = async () => {
    loading.value = true
    try {
      const organizationId = currentOrganizationStore.organizationId
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      // Buscar clientes
      const fetchedCustomers = await customerStore.fetchCustomers()
      console.log('Fetched customers:', fetchedCustomers)
      
      // Para cada cliente, buscar os nomes dos centros de custo e tipos
      const customersWithCostInfo = await Promise.all(
        fetchedCustomers.map(async (customer) => {
          if (customer.costCenters?.length) {
            try {
              // Buscar todos os centros de custo de uma vez
              const allCostCenters = await costCenterStore.fetchCostCenters(organizationId)
              const allCostTypes = await costTypeStore.fetchCostTypes(organizationId)
              
              console.log('All cost centers:', allCostCenters)
              console.log('All cost types:', allCostTypes)
              
              if (!allCostCenters || !allCostTypes) {
                console.error('Cost centers or types not found')
                return customer
              }
              
              const costCentersWithNames = customer.costCenters.map(cc => {
                const costCenter = allCostCenters.find(c => c.id === cc.costCenterId)
                const costType = allCostTypes.find(t => t.id === cc.costTypeId)
                
                console.log('Found cost center:', costCenter)
                console.log('Found cost type:', costType)
                
                return {
                  ...cc,
                  costCenterName: costCenter?.name || 'Centro de custo não encontrado',
                  costTypeName: costType?.name || 'Tipo não encontrado'
                }
              })
              
              customer.costCenters = costCentersWithNames
            } catch (error) {
              console.error('Error fetching cost info:', error)
            }
          }
          return customer
        })
      )

      customers.value = customersWithCostInfo
    } catch (error) {
      console.error('Erro ao buscar clientes:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const addCustomer = async (customer: Customer) => {
    try {
      loading.value = true
      const newCustomer = await customerStore.addCustomer(customer)
      if (newCustomer) {
        customers.value.push(newCustomer)
      }
      return newCustomer
    } finally {
      loading.value = false
    }
  }

  const updateCustomer = async (id: string, customer: Partial<Customer>) => {
    try {
      loading.value = true
      const updatedCustomer = await customerStore.updateCustomer(id, customer)
      if (updatedCustomer) {
        const index = customers.value.findIndex(c => c.id === id)
        if (index !== -1) {
          customers.value[index] = { ...customers.value[index], ...updatedCustomer }
        }
      }
      return updatedCustomer
    } finally {
      loading.value = false
    }
  }

  const deleteCustomer = async (id: string) => {
    try {
      loading.value = true
      await customerStore.deleteCustomer(id)
      customers.value = customers.value.filter(c => c.id !== id)
      return true
    } finally {
      loading.value = false
    }
  }

  const addCostCenterToCustomer = async (
    customerId: string, 
    costCenterId: string, 
    costTypeId: string, 
    costCenterName: string,
    costTypeName: string
  ) => {
    try {
      loading.value = true
      const customer = customers.value.find(c => c.id === customerId)
      
      if (!customer) {
        throw new Error('Cliente não encontrado')
      }

      if (!customer.costCenters) {
        customer.costCenters = []
      }

      // Verificar se já existe
      const existingIndex = customer.costCenters.findIndex(
        cc => cc.costCenterId === costCenterId && cc.costTypeId === costTypeId
      )

      if (existingIndex >= 0) {
        // Atualizar os nomes caso já exista
        customer.costCenters[existingIndex] = {
          costCenterId,
          costTypeId,
          costCenterName,
          costTypeName
        }
      } else {
        // Adicionar novo
        customer.costCenters.push({
          costCenterId,
          costTypeId,
          costCenterName,
          costTypeName
        })
      }

      // Atualizar no banco
      await customerStore.updateCustomer(customerId, {
        costCenters: customer.costCenters
      })

      return customer
    } finally {
      loading.value = false
    }
  }

  const removeCostCenterFromCustomer = async (
    customerId: string,
    costCenterId: string,
    costTypeId: string
  ) => {
    try {
      loading.value = true
      const customer = customers.value.find(c => c.id === customerId)
      
      if (!customer || !customer.costCenters) {
        throw new Error('Cliente não encontrado ou sem centros de custo')
      }

      customer.costCenters = customer.costCenters.filter(
        cc => !(cc.costCenterId === costCenterId && cc.costTypeId === costTypeId)
      )

      await customerStore.updateCustomer(customerId, {
        costCenters: customer.costCenters
      })

      return customer
    } finally {
      loading.value = false
    }
  }

  return {
    customers,
    loading,
    fetchCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addCostCenterToCustomer,
    removeCostCenterFromCustomer
  }
})